import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageCircle, Send, X, Loader2 } from "@/components/icons";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Botão "Cobrar no WhatsApp" (Gerente / Diretor / Diretor Geral).
 *
 * Fluxo (pedido do usuário):
 *   1. Clique abre um mini-form com mensagem editável + prazo (Hoje/Amanhã/Data).
 *   2. "Registrar e abrir WhatsApp":
 *        - grava cobrança em `/api/cobrancas-whatsapp` (aparece no sino do
 *          vendedor e no histórico do cliente);
 *        - abre `wa.me/<fone>?text=...` com o texto padronizado (nome do
 *          cliente, cobrança, prazo e link direto para o sistema).
 *
 * Regras:
 *   - Só aparece para gestor/diretor/diretor geral; nunca para vendedor.
 *   - RBAC valida na API que o vendedor pertence ao escopo do gestor.
 *   - Sem telefone do vendedor → botão desativado com "Cadastre o celular
 *     do vendedor em Usuários".
 *   - `origem` controla o texto sugerido no form (atencao/cliente/equipe).
 *
 * Props:
 *   - vendedor: { id, name, phone }
 *   - cliente:  { id, nome } (opcional)
 *   - motivo:   contexto textual (ex.: "sem contato há 12 dias")
 *   - origem:   "cliente" | "atencao" | "equipe" (default "cliente")
 *   - variant:  "primary" | "ghost" | "icon"
 *   - testId:   sufixo p/ data-testid
 */
const PRAZO_HOJE = "hoje";
const PRAZO_AMANHA = "amanha";
const PRAZO_DATA = "data";

const pad = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function formatPrazo(iso, opt) {
  const [y, m, d] = iso.split("-");
  const dm = `${d}/${m}`;
  if (opt === PRAZO_HOJE) return `hoje, ${dm}`;
  if (opt === PRAZO_AMANHA) return `amanhã, ${dm}`;
  return dm;
}

export default function CobrarWhatsApp({ vendedor, cliente, motivo, origem = "cliente", variant = "primary", testId = "" }) {
  const { user } = useAuth();
  const gestor = ["manager", "director", "diretor_geral", "admin"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [prazoOpt, setPrazoOpt] = useState(PRAZO_HOJE);
  const [prazoDate, setPrazoDate] = useState("");
  const [busy, setBusy] = useState(false);
  // Guarda a aba aberta no gesto do usuário para que a URL do WhatsApp
  // seja definida depois (iOS Safari e Chrome Android bloqueiam window.open
  // chamado após uma Promise). Essa é a orientação do suporte Emergent.
  const waWindowRef = useRef(null);

  if (!gestor) return null;
  if (!vendedor || !vendedor.id) return null;
  if (vendedor.id === user?.id) return null;

  const temFone = !!(vendedor.phone && String(vendedor.phone).replace(/\D+/g, "").length >= 10);
  const primeiroNomeVendedor = (vendedor.name || "").trim().split(/\s+/)[0] || "vendedor";
  const primeiroNomeGestor = (user?.name || "").trim().split(/\s+/)[0] || "Gestor";

  const _mensagemPadrao = () => {
    // Sugestão contextual conforme origem (pedido do usuário).
    if (origem === "atencao") {
      const dias = /(\d+)\s*dias?/i.exec(motivo || "");
      const n = dias ? dias[1] : null;
      return n
        ? `Cliente sem contato há ${n} dias. Faça o contato e registre no sistema.`
        : "Cliente sem contato há muitos dias. Faça o contato e registre no sistema.";
    }
    if (origem === "equipe") {
      return "Preciso de atualização da sua carteira.";
    }
    // cliente (default)
    return "Preciso de retorno sobre este cliente.";
  };

  const abrir = () => {
    if (!temFone) {
      // Não bloqueia o botão: mostra o aviso para a falha não ficar silenciosa
      // (orientação do suporte Emergent).
      toast.info("Cadastre o celular do vendedor em Usuários");
      return;
    }
    setMsg(_mensagemPadrao());
    setPrazoOpt(PRAZO_HOJE);
    // default prazoDate = hoje (ISO) para quando "Data" for escolhida.
    setPrazoDate(toISODate(new Date()));
    setOpen(true);
  };

  const _resolveIsoPrazo = () => {
    if (prazoOpt === PRAZO_HOJE) return toISODate(new Date());
    if (prazoOpt === PRAZO_AMANHA) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return toISODate(d);
    }
    return prazoDate || toISODate(new Date());
  };

  const _montarTexto = ({ isoPrazo, cobrancaId }) => {
    const link = `${window.location.origin}/solicitacao/${cobrancaId}`;
    const linhas = [
      `Olá, ${primeiroNomeVendedor}! Registrei uma solicitação para você no MPires Connect.`,
      "",
    ];
    if (cliente?.nome) linhas.push(`Cliente: ${cliente.nome}`);
    linhas.push(`Solicitação: ${msg.trim()}`);
    linhas.push(`Prazo: ${formatPrazo(isoPrazo, prazoOpt)}`);
    linhas.push("");
    linhas.push(`Abra no sistema: ${link}`);
    linhas.push("");
    linhas.push(primeiroNomeGestor);
    return linhas.join("\n");
  };

  const enviar = async () => {
    const texto = (msg || "").trim();
    if (!texto) { toast.error("Escreva a solicitação"); return; }
    if (prazoOpt === PRAZO_DATA && !prazoDate) { toast.error("Escolha a data do prazo"); return; }
    // LOTE W-1: dupla trava contra toque duplo (cobrança duplicada) + aviso
    // tardio se o estado mudar entre `abrir()` e `enviar()`.
    if (busy) return;
    if (!temFone) { toast.error("Vendedor sem telefone cadastrado"); return; }
    // LOTE W-1: abre a aba AINDA no gesto do usuário, mas SEM "noopener" —
    // `window.open(..., "noopener,noreferrer")` devolve `null`, então não
    // conseguíamos atualizar `location.href` depois do await e o fallback
    // `window.open(wa.me)` era bloqueado pelo navegador, deixando o
    // `about:blank` aberto. Agora guardamos a referência real e removemos
    // `opener` por segurança (equivalente a `noopener` sem perder a ref).
    const w = window.open("about:blank", "_blank");
    if (w) {
      try { w.opener = null; } catch { /* ignore */ }
      waWindowRef.current = w;
    } else {
      waWindowRef.current = null;
    }
    setBusy(true);
    try {
      const isoPrazo = _resolveIsoPrazo();
      const { data: cobranca } = await api.post("/cobrancas-whatsapp", {
        vendedor_id: vendedor.id,
        cliente_id: cliente?.id || null,
        text: texto,
        prazo: isoPrazo,
        origem,
      });
      const textoWpp = _montarTexto({ isoPrazo, cobrancaId: cobranca.id });
      const fone = String(vendedor.phone).replace(/\D+/g, "");
      const wa = fone.length === 11 || fone.length === 10 ? `55${fone}` : fone;
      const url = `https://wa.me/${wa}?text=${encodeURIComponent(textoWpp)}`;
      if (waWindowRef.current && !waWindowRef.current.closed) {
        waWindowRef.current.location.href = url;
      } else {
        // Pop-up bloqueado no gesto → navega na mesma aba (último recurso,
        // nunca mais `window.open` depois de `await` porque é bloqueado).
        window.location.href = url;
      }
      toast.success("Solicitação registrada");
      setOpen(false);
    } catch (err) {
      // Falhou o registro: fecha a aba vazia para não deixar `about:blank`
      // aberto, e mostra o erro na tela.
      if (waWindowRef.current && !waWindowRef.current.closed) {
        try { waWindowRef.current.close(); } catch { /* ignore */ }
      }
      waWindowRef.current = null;
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const _cls = () => {
    if (variant === "ghost") return "inline-flex items-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-lg bg-white text-navy border border-[#D5DEE5] hover:bg-slate-50";
    if (variant === "icon") return "inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white text-navy border border-[#D5DEE5] hover:bg-slate-50 shrink-0";
    // Padrão agora é secundário (branco + borda discreta) — só o ícone fica verde.
    return "inline-flex items-center gap-1.5 h-[38px] px-4 text-sm font-semibold rounded-lg bg-white text-navy border border-[#D5DEE5] hover:bg-slate-50";
  };

  const prazoBtn = (opt, label) => (
    <button
      type="button"
      key={opt}
      onClick={() => setPrazoOpt(opt)}
      data-testid={`cobrar-wpp-prazo-${opt}`}
      className={`h-8 px-3 text-xs font-medium rounded-lg border transition-colors ${prazoOpt === opt ? "bg-brand text-white border-brand" : "border-input text-navy hover:bg-slate-50"}`}
    >
      {label}
    </button>
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); abrir(); }}
        title={temFone ? "Pedir retorno no WhatsApp" : "Cadastre o celular do vendedor em Usuários"}
        aria-label="Pedir retorno no WhatsApp"
        aria-disabled={!temFone}
        data-testid={`cobrar-wpp${testId ? "-" + testId : ""}`}
        className={`${_cls()} ${!temFone ? "opacity-70" : ""}`}
      >
        <MessageCircle className="w-4 h-4" style={{ color: "#1E7A55" }} />
        {variant !== "icon" && <span>Pedir retorno no WhatsApp</span>}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="cobrar-wpp-modal"
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-base font-semibold text-navy">Pedir retorno</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Vai para <b>{vendedor.name}</b>
                  {cliente?.nome ? <> · <b>{cliente.nome}</b></> : null}
                </p>
              </div>
              <button onClick={() => !busy && setOpen(false)} className="text-slate-400 hover:text-navy" aria-label="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>

            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              O que você precisa
            </label>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={3}
              className="w-full mt-1 rounded-lg border border-input px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="cobrar-wpp-msg"
            />

            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-3 block">
              Prazo
            </label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {prazoBtn(PRAZO_HOJE, "Hoje")}
              {prazoBtn(PRAZO_AMANHA, "Amanhã")}
              {prazoBtn(PRAZO_DATA, "Escolher data")}
              {prazoOpt === PRAZO_DATA && (
                <input
                  type="date"
                  value={prazoDate}
                  onChange={(e) => setPrazoDate(e.target.value)}
                  data-testid="cobrar-wpp-prazo-data-input"
                  className="h-8 px-2 text-xs rounded-lg border border-input"
                />
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} disabled={busy}
                className="h-9 px-3.5 text-sm font-medium rounded-lg border border-input text-navy hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={enviar} disabled={busy}
                data-testid="cobrar-wpp-enviar"
                className="h-[38px] px-4 text-sm font-semibold rounded-lg bg-brand text-white hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" style={{ color: "#FFFFFF" }} />}
                Registrar e abrir WhatsApp
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
